import React from "react";
import { Link } from "react-router-dom";
import { Skeleton } from "@/components/ui/skeleton";
import { SITE, siteApi, useLive, useCompany, companyLine, usePageMeta } from "@/lib/site";
import { PageHeader, Section, InfoCard, Prose, WaitlistForm } from "@/components/site/SiteBits";

const Mail = ({ subject, children }) => (
  <a href={`mailto:${SITE.email}${subject ? `?subject=${encodeURIComponent(subject)}` : ""}`} className="font-semibold text-primary hover:underline">
    {children || SITE.email}
  </a>
);

export function AboutPage() {
  usePageMeta("About", "Fable + is a management-first super-app bringing local businesses into one place — one account, many worlds.");
  const company = useCompany();
  return (
    <div>
      <PageHeader kicker="About" title="About Fable +" lede="Fable + is a management-first super-app that brings the local businesses you love into one place: one account, many worlds." />
      <Section>
        <div className="grid gap-4 md:grid-cols-3">
          <InfoCard title="Our mission">Make discovering, booking and managing local services effortless, for customers and for the independent businesses that serve them.</InfoCard>
          <InfoCard title="One account, many worlds">Services, Education, Rentals, Food, Casting and Experiences, each with booking flows designed for how that world actually works.</InfoCard>
          <InfoCard title="Built for trust">Verified providers, transparent pricing, secure payments, reviews and in-app messaging keep every interaction safe and simple.</InfoCard>
        </div>
      </Section>
      <Section alt>
        <Prose>
          <h2 style={{ marginTop: 0 }}>Who we are</h2>
          <p>Fable + is built in the UK. {companyLine(company)} We're a small team with a big surface area, shipping things customers and partners use every day.</p>
          <h2>Get in touch</h2>
          <p>We'd love to hear from you at <Mail />.</p>
        </Prose>
      </Section>
    </div>
  );
}

export function PaymentsPage() {
  usePageMeta("Payments", "How payments, deposits, escrow, receipts and refunds work on Fable +.");
  return (
    <div>
      <PageHeader kicker="Payments" title="Secure, transparent payments" lede="Payments on Fable + are processed by Stripe, and you always see the breakdown before you pay." />
      <Section>
        <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
          <InfoCard title="Secure by design">Card payments are processed by Stripe. We never store your full card number.</InfoCard>
          <InfoCard title="Deposits & escrow">Rentals may hold a refundable deposit; casting projects fund an escrow that releases on completion.</InfoCard>
          <InfoCard title="Receipts">Every payment appears in Profile → Payments &amp; receipts, including amounts paid, on hold, in escrow and refunded.</InfoCard>
          <InfoCard title="Refunds">Eligible cancellations are refunded under the policy shown for your booking, back to your original payment method.</InfoCard>
        </div>
      </Section>
    </div>
  );
}

export function CareersPage() {
  usePageMeta("Careers", "Work at Fable +: we're building the operating system for local commerce.");
  const live = useLive(() => siteApi.careers());
  const roles = live?.roles || [];
  return (
    <div>
      <PageHeader kicker="Careers" title="Build the operating system for local commerce" lede="We're always looking for people who love great products and great service." />
      {live === undefined ? (
        <Section><Skeleton className="h-32 rounded-2xl" /></Section>
      ) : roles.length > 0 ? (
        <Section title="Open roles">
          <div className="grid gap-4 md:grid-cols-2">
            {roles.map((r) => (
              <InfoCard key={r.id || r.title} title={r.title}>
                <p className="text-xs">{[r.team, r.location, r.type].filter(Boolean).join(" · ")}</p>
                {r.summary && <p className="mt-2">{r.summary}</p>}
                <a href={`mailto:${r.apply_email || SITE.email}?subject=${encodeURIComponent(`Application: ${r.title}`)}`} className="mt-3 inline-block font-semibold text-primary hover:underline">Apply →</a>
              </InfoCard>
            ))}
          </div>
        </Section>
      ) : null}
      <Section alt={roles.length > 0}>
        <div className="grid gap-4 md:grid-cols-3">
          <InfoCard title="Why Fable +">Small team, big surface area, real impact. You'll ship things customers and partners use every day.</InfoCard>
          <InfoCard title="How we work">Remote-friendly, low-ego, high-ownership. We value craft, speed and kindness in equal measure.</InfoCard>
          <InfoCard title="Say hello">Send your story and anything you're proud of to <Mail subject="Careers" /> with the subject "Careers".</InfoCard>
        </div>
      </Section>
    </div>
  );
}

export function ContactPage() {
  usePageMeta("Contact", "Contact Fable +.");
  const company = useCompany();
  return (
    <div>
      <PageHeader kicker="Contact" title="Get in touch" lede="Questions, partnerships, press or support: we read every message." />
      <Section>
        <div className="grid gap-4 md:grid-cols-3">
          <InfoCard title="Email"><Mail /></InfoCard>
          <InfoCard title="Privacy requests">Download or delete your data in the app under Profile → Privacy, or email us with the subject "Privacy".</InfoCard>
          <InfoCard title="Bookings">For a question about a booking, message the provider in the app first; they're responsible for the service.</InfoCard>
        </div>
        <p className="mt-8 max-w-3xl text-sm text-muted-foreground">{companyLine(company)}</p>
      </Section>
    </div>
  );
}

export function GetTheAppPage() {
  usePageMeta("Get the app", "Fable + is launching soon on iPhone and Android.");
  return (
    <div>
      <PageHeader kicker="Get the app" title="Launching soon on iPhone and Android" lede="Fable + is in final testing ahead of our UK launch. You can already browse and book on the web." />
      <Section>
        <div className="grid items-start gap-4 md:grid-cols-2">
          <div className="rounded-2xl border border-border bg-card p-6">
            <h3 className="text-lg font-bold">Get early access</h3>
            <p className="mb-4 mt-1.5 text-sm text-muted-foreground">Be first to know when Fable + is in the App Store and Google Play.</p>
            <WaitlistForm interest="customer" />
          </div>
          <InfoCard title="Run a business?">
            <p>Register now and we'll help you set up before launch, so customers can find you from day one.</p>
            <Link to="/business#register" className="mt-3 inline-block font-semibold text-primary hover:underline">Register your business →</Link>
          </InfoCard>
        </div>
      </Section>
    </div>
  );
}
